/** @jsxImportSource @emotion/react */
import React from "react";
import { Alert, Col, Form, Input, Modal, Row, Button, Radio } from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Styles from "./styles";
import {
  TenantUserUsecase,
  useTenantUserDispatch,
} from "../../../usecases/TenantUser";
import { CreateTenantUserDto } from "../../../services/TenantUser/dto/create.dto";
import { tenantUserFormSchema } from "./formSchema";

interface ModalAddTenantUserProps {
  onCancel: () => void;
  onSuccess: () => void;
  visible: boolean;
}

const ModalAddTenantUserComponent: React.FC<ModalAddTenantUserProps> = ({
  visible,
  onCancel,
  onSuccess,
}: ModalAddTenantUserProps) => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<CreateTenantUserDto>({
    mode: "onSubmit",
    resolver: yupResolver(tenantUserFormSchema),
  });
  const TenantUserDispatch = useTenantUserDispatch();

  const handleSubmitHandle = React.useCallback(
    async (data: CreateTenantUserDto) => {
      setIsProcessing(true);
      try {
        if (TenantUserDispatch) {
          const tenantUserUsecase = new TenantUserUsecase(TenantUserDispatch);
          await tenantUserUsecase.create(data);
        }
        reset();
        setErrorMessage("");
        onSuccess();
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
      } finally {
        setIsProcessing(false);
      }
    },
    [TenantUserDispatch, reset, onSuccess],
  );

  const onCancelHandle = React.useCallback(() => {
    reset();
    setErrorMessage("");
    onCancel();
  }, [onCancel, reset]);

  return (
    <Modal
      title="Add New User"
      visible={visible}
      closable={false}
      footer={[
        <Button key="back" size="large" onClick={onCancelHandle}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="large"
          loading={isProcessing}
          onClick={handleSubmit(handleSubmitHandle)}
        >
          Add
        </Button>,
      ]}
    >
      <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
        {errorMessage && (
          <Row justify="center">
            <Col css={Styles.colGutterStyle}>
              <Alert message={errorMessage} type="error" />
            </Col>
          </Row>
        )}
        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              label="First Name"
              css={Styles.labelStyle}
              validateStatus={errors.userFirstName ? "error" : "validating"}
              help={errors.userFirstName?.message}
            >
              <Controller
                name="userFirstName"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" allowClear />
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              label="Last Name"
              css={Styles.labelStyle}
              validateStatus={errors.userLastName ? "error" : "validating"}
              help={errors.userLastName?.message}
            >
              <Controller
                name="userLastName"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" allowClear />
                )}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Email"
          css={Styles.labelStyle}
          validateStatus={errors.userEmail ? "error" : "validating"}
          help={errors.userEmail?.message}
        >
          <Controller
            name="userEmail"
            control={control}
            render={({ field }) => <Input {...field} size="large" allowClear />}
          />
        </Form.Item>
        <Form.Item
          label="User Role"
          css={Styles.labelStyle}
          validateStatus={errors.userRole ? "error" : "validating"}
          help={errors.userRole?.message}
        >
          <Controller
            name="userRole"
            control={control}
            render={({ field }) => (
              <Radio.Group {...field}>
                <Radio value="Super Moderator">Super Moderator</Radio>
                <Radio value="Moderator">Moderator</Radio>
                <Radio value="Sale">Sale</Radio>
              </Radio.Group>
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const ModalAddTenantUser = React.memo(ModalAddTenantUserComponent);
